import React, { useState, useEffect, useRef } from "react";
import { 
    Phone, Send, User, Mic, Video, Ticket, CheckCircle, 
    Clock, Search, MoreHorizontal, Paperclip, PhoneCall, 
    X, Shield, Lock, MessageSquare, Monitor, FileText,
    Calendar, StopCircle, Play, Film, Download
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { helpDeskAPI, helpDeskWebSocket, helpDeskUtils } from "../services/employee.service";

// --- Styles & Fonts Injection ---
const GlobalStyles = () => (
    <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Roboto:wght@300;400;500;700&display=swap');
        
        .font-heading { font-family: 'Poppins', sans-serif; }
        .font-body { font-family: 'Roboto', sans-serif; }
        
        /* Custom Scrollbar for sleek look */
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #f1f5f9; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        
        /* Animation for recording pulse */
        @keyframes pulse-red {
            0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
            70% { box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
            100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
        }
        .animate-pulse-red {
            animation: pulse-red 2s infinite;
        }
    `}</style>
);

// --- Mock Data (Updated with specific Dates for filtering) ---
const MOCK_TICKETS = [
    { 
        id: "TKT-2024-001", 
        employee: "Aarav Sharma", 
        role: "Senior Developer",
        email: "aarav.s@company.com",
        subject: "Discrepancy in October Salary Slip", 
        status: "Open", 
        priority: "High",
        lastUpdate: "5 mins ago", 
        ticketDate: "2023-10-24",
        department: "Engineering", 
        contact: "+91 98765 43210",
        assets: ["MacBook Pro M2", "Dell Monitor"],
        avatarColor: "bg-blue-500"
    },
    { 
        id: "TKT-2024-002", 
        employee: "Priya Verma", 
        role: "UX Designer",
        email: "priya.v@company.com",
        subject: "Request for Adobe Cloud License Renewal", 
        status: "Pending", 
        priority: "Medium",
        lastUpdate: "1 hour ago", 
        ticketDate: "2023-10-24",
        department: "Design", 
        contact: "+91 98765 43211",
        assets: ["iMac 27\"", "Wacom Tablet"],
        avatarColor: "bg-purple-500"
    },
    { 
        id: "TKT-2024-003", 
        employee: "Vikram Singh", 
        role: "Sales Manager",
        email: "vikram.s@company.com",
        subject: "Attendance regularization for On-Duty travel", 
        status: "Open", 
        priority: "Low",
        lastUpdate: "4 hours ago", 
        ticketDate: "2023-10-23",
        department: "Sales", 
        contact: "+91 98765 43212",
        assets: ["Lenovo ThinkPad"],
        avatarColor: "bg-green-500"
    },
    { 
        id: "TKT-2024-004", 
        employee: "Sneha Reddy", 
        role: "HR Intern",
        email: "sneha.r@company.com",
        subject: "VPN Access Denied Error 404", 
        status: "Closed", 
        priority: "High",
        lastUpdate: "1 day ago", 
        ticketDate: "2023-10-22",
        department: "HR", 
        contact: "+91 98765 43213",
        assets: ["HP Pavilion"],
        avatarColor: "bg-pink-500"
    },
    { 
        id: "TKT-2024-005", 
        employee: "Rohan Das", 
        role: "Marketing Exec",
        email: "rohan.d@company.com",
        subject: "Leave Balance Correction", 
        status: "Closed", 
        priority: "Low",
        lastUpdate: "3 days ago", 
        ticketDate: "2023-10-20",
        department: "Marketing", 
        contact: "+91 98765 43214",
        assets: ["Dell XPS"],
        avatarColor: "bg-orange-500"
    },
];

const INITIAL_CHATS = {
    "TKT-2024-001": [
        { id: 1, type: "employee", contentType: "text", text: "Hi Admin, I noticed a deduction in my Oct salary slip labeled 'Unpaid Leave', but I was present. Can you check?", time: "10:30 AM" },
        { id: 2, type: "system", contentType: "text", text: "Ticket assigned to Payroll Team.", time: "10:31 AM" },
    ],
    "TKT-2024-002": [
        { id: 1, type: "employee", contentType: "text", text: "My Adobe license expired today. I need it for the new project.", time: "09:15 AM" },
    ]
};

// --- Helper Components ---
const Avatar = ({ name, color, size = "md" }) => {
    const initials = name ? name.split(" ").map(n => n[0]).join("").substring(0, 2) : "??";
    const sizeClasses = size === "lg" ? "w-16 h-16 text-xl" : size === "sm" ? "w-8 h-8 text-xs" : "w-10 h-10 text-sm";
    
    return (
        <div className={`${sizeClasses} ${color} rounded-full flex items-center justify-center text-white font-bold shadow-sm flex-shrink-0`}>
            {initials}
        </div>
    );
};

export default function AdminHelpDesk() {
    // --- State ---
    const [tickets, setTickets] = useState([]);
    const [activeTicketId, setActiveTicketId] = useState(null);
    const [chats, setChats] = useState({});
    const [inputValue, setInputValue] = useState("");
    const [activeTab, setActiveTab] = useState("Open"); // Open, Pending, Closed
    const [isInternalNote, setIsInternalNote] = useState(false);
    const [loading, setLoading] = useState(true);
    const [dashboardStats, setDashboardStats] = useState({});
    
    // Call State
    const [callStatus, setCallStatus] = useState("idle"); // idle, connecting, active
    const [callDuration, setCallDuration] = useState(0);

    // Media/Recording State
    const [isRecordingAudio, setIsRecordingAudio] = useState(false);
    const [isRecordingVideo, setIsRecordingVideo] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);

    // Filter State
    const [filterDate, setFilterDate] = useState(""); // YYYY-MM-DD

    // Refs
    const chatEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const recordingIntervalRef = useRef(null);

    // --- Derived State ---
    const activeTicket = tickets.find(t => t.id === activeTicketId);
    const activeMessages = chats[activeTicketId] || [];

    // Filter Logic
    const filteredTickets = tickets.filter(t => {
        // Tab Filter
        const matchesTab = activeTab === "All" 
            ? true 
            : activeTab === "Open" 
                ? (t.status === "Open" || t.status === "Pending" || t.status === "In Progress") 
                : t.status === activeTab;
        
        // Date Filter (Only applies if date is selected AND we are in Closed tab/history mode)
        const matchesDate = (activeTab === "Closed" && filterDate) 
            ? t.created_at?.startsWith(filterDate)
            : true;

        return matchesTab && matchesDate;
    });

    // --- Effects ---
    useEffect(() => {
        initializeHelpDesk();
        return () => {
            helpDeskWebSocket.disconnect();
        };
    }, []);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [activeMessages, activeTicketId, isRecordingAudio, isRecordingVideo]);

    // Call Timer
    useEffect(() => {
        let interval;
        if (callStatus === "active") {
            interval = setInterval(() => setCallDuration(prev => prev + 1), 1000);
        } else {
            setCallDuration(0);
        }
        return () => clearInterval(interval);
    }, [callStatus]);

    // Recording Timer
    useEffect(() => {
        if (isRecordingAudio || isRecordingVideo) {
            recordingIntervalRef.current = setInterval(() => setRecordingTime(prev => prev + 1), 1000);
        } else {
            clearInterval(recordingIntervalRef.current);
            setRecordingTime(0);
        }
        return () => clearInterval(recordingIntervalRef.current);
    }, [isRecordingAudio, isRecordingVideo]);

    // --- API Functions ---
    const initializeHelpDesk = async () => {
        try {
            setLoading(true);
            
            // Initialize WebSocket connection
            helpDeskUtils.initializeWebSocket('admin');
            
            // Set up WebSocket event handlers
            helpDeskWebSocket.onMessage('new_ticket', handleNewTicket);
            helpDeskWebSocket.onMessage('ticket_update', handleTicketUpdate);
            helpDeskWebSocket.onMessage('new_message', handleNewMessage);
            helpDeskWebSocket.onMessage('typing_indicator', handleTypingIndicator);
            
            // Load initial data
            await Promise.all([
                loadTickets(),
                loadDashboardStats()
            ]);
            
        } catch (error) {
            console.error('Failed to initialize help desk:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadTickets = async () => {
        try {
            const response = await helpDeskAPI.getAdminTickets({
                status: activeTab === "All" ? null : activeTab,
                date_filter: filterDate || null
            });
            
            if (response.data.success) {
                setTickets(response.data.data);
                
                // Set first ticket as active if none selected
                if (!activeTicketId && response.data.data.length > 0) {
                    setActiveTicketId(response.data.data[0].id);
                    loadTicketDetails(response.data.data[0].id);
                }
            }
        } catch (error) {
            console.error('Failed to load tickets:', error);
        }
    };

    const loadTicketDetails = async (ticketId) => {
        try {
            const response = await helpDeskAPI.getTicket(ticketId);
            
            if (response.data.success) {
                const ticket = response.data.data;
                setChats(prev => ({
                    ...prev,
                    [ticketId]: ticket.messages || []
                }));
                
                // Subscribe to ticket updates
                helpDeskWebSocket.subscribeToTicket(ticketId);
            }
        } catch (error) {
            console.error('Failed to load ticket details:', error);
        }
    };

    const loadDashboardStats = async () => {
        try {
            const response = await helpDeskAPI.getDashboardStats();
            if (response.data.success) {
                setDashboardStats(response.data.data);
            }
        } catch (error) {
            console.error('Failed to load dashboard stats:', error);
        }
    };

    // --- WebSocket Event Handlers ---
    const handleNewTicket = (message) => {
        const ticketData = message.data;
        setTickets(prev => [ticketData, ...prev]);
        loadDashboardStats(); // Refresh stats
    };

    const handleTicketUpdate = (message) => {
        const { ticket_id, update_type, data } = message;
        
        if (update_type === 'status_change') {
            setTickets(prev => prev.map(ticket => 
                ticket.id === ticket_id 
                    ? { ...ticket, status: data.new_status, updated_at: new Date().toISOString() }
                    : ticket
            ));
        } else if (update_type === 'assignment_change') {
            setTickets(prev => prev.map(ticket => 
                ticket.id === ticket_id 
                    ? { ...ticket, assigned_agent_name: data.assigned_agent_name, updated_at: new Date().toISOString() }
                    : ticket
            ));
        }
    };

    const handleNewMessage = (message) => {
        const messageData = message.data;
        setChats(prev => ({
            ...prev,
            [messageData.ticket_id]: [...(prev[messageData.ticket_id] || []), messageData]
        }));
        
        // Update ticket's last message time
        setTickets(prev => prev.map(ticket => 
            ticket.id === messageData.ticket_id 
                ? { ...ticket, last_message_at: messageData.created_at, updated_at: messageData.created_at }
                : ticket
        ));
    };

    const handleTypingIndicator = (message) => {
        // Handle typing indicators (could show "User is typing...")
        console.log('Typing indicator:', message);
    };


    // --- Formatters ---
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // --- Handlers ---

    // 1. Sending Text Message
    const handleSendMessage = async () => {
        if (!inputValue.trim() || !activeTicketId) return;
        
        try {
            const messageType = isInternalNote ? "internal" : "admin";
            await helpDeskAPI.sendMessage(activeTicketId, inputValue, messageType, isInternalNote);
            setInputValue("");
            
            // Send typing indicator (stop typing)
            helpDeskWebSocket.sendTyping(activeTicketId, false);
            
        } catch (error) {
            console.error('Failed to send message:', error);
        }
    };

    // 2. Handle ticket selection
    const handleTicketSelect = async (ticketId) => {
        if (activeTicketId) {
            helpDeskWebSocket.unsubscribeFromTicket(activeTicketId);
        }
        
        setActiveTicketId(ticketId);
        await loadTicketDetails(ticketId);
    };

    // 3. Handle status change
    const handleStatusChange = async (ticketId, newStatus) => {
        try {
            await helpDeskAPI.updateTicketStatus(ticketId, newStatus);
            // WebSocket will handle the UI update
        } catch (error) {
            console.error('Failed to update ticket status:', error);
        }
    };

    // 4. Handle ticket assignment
    const handleAssignTicket = async (ticketId, agentEmpCode, agentName) => {
        try {
            await helpDeskAPI.assignTicket(ticketId, agentEmpCode, agentName);
            // WebSocket will handle the UI update
        } catch (error) {
            console.error('Failed to assign ticket:', error);
        }
    };

    // 5. Handle file upload
    const handleFileUpload = async (file) => {
        if (!activeTicketId || !file) return;
        
        try {
            await helpDeskAPI.uploadFile(activeTicketId, file, "admin");
            // WebSocket will handle the UI update
        } catch (error) {
            console.error('Failed to upload file:', error);
        }
    };

    // 6. Handle typing indicator
    const handleInputChange = (e) => {
        setInputValue(e.target.value);
        
        // Send typing indicator
        if (activeTicketId) {
            helpDeskWebSocket.sendTyping(activeTicketId, e.target.value.length > 0);
        }
    };

    // 7. Handle tab change
    const handleTabChange = (newTab) => {
        setActiveTab(newTab);
        loadTickets(); // Reload tickets with new filter
    };

    // 8. Handle call initiation
    const handleInitiateCall = (recipientEmpCode, callType = 'voice') => {
        if (activeTicketId) {
            helpDeskWebSocket.initiateCall(activeTicketId, recipientEmpCode, callType);
            setCallStatus('connecting');
        }
    };

    const formatMessageTime = (timestamp) => {
        return new Date(timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    };

    // 3. Call Logic
    const handleStartCall = () => {
        if (!activeTicket?.employee_contact) {
            alert("No contact number available for this employee.");
            return;
        }
        handleInitiateCall(activeTicket.emp_code, 'voice');
    };

    const handleEndCall = () => {
        setCallStatus("idle");
        // Add system message about call end
        const callEndMessage = {
            id: Date.now(),
            message_type: "system",
            content_type: "text",
            content: `Call with ${activeTicket.employee_name} ended. Duration: ${formatTime(callDuration)}.`,
            created_at: new Date().toISOString(),
            sender_name: "System"
        };
        setChats(prev => ({
            ...prev,
            [activeTicketId]: [...(prev[activeTicketId] || []), callEndMessage]
        }));
    };

    // 4. File Attachment Logic
    const handleFileIconClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            await handleFileUpload(file);
        }
    };

    // 5. Mic & Video Logic (Start/Stop)
    const toggleAudioRecording = () => {
        if (isRecordingAudio) {
            // Stop recording -> Send Audio Message
            setIsRecordingAudio(false);
            const audioMessage = {
                id: Date.now(),
                message_type: "admin",
                content_type: "audio",
                content: `Audio message recorded (${formatTime(recordingTime)})`,
                created_at: new Date().toISOString(),
                sender_name: "Admin",
                file_name: `audio_${Date.now()}.mp3`,
                file_size: "2.1 MB"
            };
            setChats(prev => ({
                ...prev,
                [activeTicketId]: [...(prev[activeTicketId] || []), audioMessage]
            }));
        } else {
            // Start recording
            setIsRecordingVideo(false); // Ensure video is off
            setIsRecordingAudio(true);
        }
    };

    const toggleVideoRecording = () => {
        if (isRecordingVideo) {
            // Stop recording -> Send Video Message
            setIsRecordingVideo(false);
            const videoMessage = {
                id: Date.now(),
                message_type: "admin",
                content_type: "video",
                content: `Video message recorded (${formatTime(recordingTime)})`,
                created_at: new Date().toISOString(),
                sender_name: "Admin",
                file_name: `video_${Date.now()}.mp4`,
                file_size: "5.3 MB"
            };
            setChats(prev => ({
                ...prev,
                [activeTicketId]: [...(prev[activeTicketId] || []), videoMessage]
            }));
        } else {
            // Start recording
            setIsRecordingAudio(false); // Ensure audio is off
            setIsRecordingVideo(true);
        }
    };

    const handleResolve = async () => {
        if (activeTicketId) {
            await handleStatusChange(activeTicketId, "Resolved");
        }
    };

    // --- Sub-Components for Messages ---
    const RenderMessageContent = ({ msg }) => {
        if (msg.content_type === "text") return <p>{msg.content}</p>;
        
        if (msg.content_type === "file") return (
            <div className="flex items-center gap-3 bg-white/10 p-2 rounded-lg border border-black/5">
                <div className="bg-indigo-100 p-2 rounded text-indigo-600">
                    <FileText size={20} />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-medium text-xs truncate">{msg.file_name}</p>
                    <p className="text-[10px] opacity-70">{msg.file_size}</p>
                </div>
                <button className="text-indigo-600 hover:text-indigo-800 p-1"><Download size={16}/></button>
            </div>
        );

        if (msg.content_type === "audio") return (
            <div className="flex items-center gap-3 min-w-[200px]">
                <div className="bg-white/20 p-2 rounded-full cursor-pointer hover:bg-white/30 transition">
                    <Play size={16} fill="currentColor" />
                </div>
                <div className="flex-1 h-1 bg-black/20 rounded-full overflow-hidden">
                    <div className="w-1/3 h-full bg-current"></div>
                </div>
                <span className="text-[10px] font-mono">{msg.file_size || "0:30"}</span>
            </div>
        );

        if (msg.content_type === "video") return (
            <div className="min-w-[200px]">
                <div className="flex items-center gap-2 mb-2 text-[10px] font-bold uppercase tracking-wider opacity-70">
                    <Film size={12} /> Video Message
                </div>
                <div className="h-24 bg-black/10 rounded-lg flex items-center justify-center border border-black/5 relative overflow-hidden group cursor-pointer">
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                         <Play size={24} fill="white" className="text-white"/>
                    </div>
                    <Video size={32} className="text-current opacity-20" />
                </div>
                <p className="text-[10px] mt-1 text-right">{msg.file_size || "0:45"}</p>
            </div>
        );

        return null;
    };

    return (
        <div className="flex h-screen w-full bg-[#f3f4f6] text-gray-800 overflow-hidden font-body">
            <GlobalStyles />
            <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileChange} />

            {/* --- LEFT PANEL: Queue --- */}
            <div className="w-80 bg-white border-r border-gray-200 flex flex-col flex-shrink-0 z-10 shadow-sm">
                <div className="p-4 border-b border-gray-100">
                    <h1 className="font-heading font-bold text-xl text-indigo-900 flex items-center gap-2">
                        <Shield className="w-6 h-6 text-indigo-600" /> HRMS Desk
                    </h1>
                </div>

                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 space-y-3">
                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                        <input 
                            type="text" 
                            placeholder="Search tickets..." 
                            className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                        />
                    </div>
                    {/* Tabs */}
                    <div className="flex gap-2 p-1 bg-gray-200/50 rounded-lg">
                        {["Open", "Closed"].map(tab => (
                            <button
                                key={tab}
                                onClick={() => handleTabChange(tab)}
                                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all font-heading ${
                                    activeTab === tab ? "bg-white shadow text-indigo-700" : "text-gray-500 hover:text-gray-700"
                                }`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                    
                    {/* Date Filter (Only in Closed Tab) */}
                    <AnimatePresence>
                        {activeTab === "Closed" && (
                            <motion.div 
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                            >
                                <div className="relative">
                                    <Calendar className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                                    <input 
                                        type="date"
                                        value={filterDate}
                                        onChange={(e) => setFilterDate(e.target.value)}
                                        className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 outline-none text-gray-600"
                                    />
                                    {filterDate && (
                                        <button 
                                            onClick={() => setFilterDate("")}
                                            className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                                        >
                                            <X size={14} />
                                        </button>
                                    )}
                                </div>
                                <p className="text-[10px] text-gray-400 mt-1 pl-1">Filter history by date</p>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto p-2 space-y-2">
                    {loading ? (
                        <div className="p-8 text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                            <p className="text-gray-500 text-sm">Loading tickets...</p>
                        </div>
                    ) : filteredTickets.length > 0 ? filteredTickets.map(ticket => (
                        <div 
                            key={ticket.id}
                            onClick={() => handleTicketSelect(ticket.id)}
                            className={`p-3 rounded-xl cursor-pointer border transition-all duration-200 ${
                                activeTicketId === ticket.id 
                                ? "bg-indigo-50 border-indigo-200 shadow-sm" 
                                : "bg-white border-transparent hover:bg-gray-50 hover:border-gray-200"
                            }`}
                        >
                            <div className="flex justify-between items-start mb-1">
                                <span className="font-heading font-semibold text-sm text-gray-800 line-clamp-1">{ticket.subject}</span>
                                {(ticket.status === "Open" || ticket.status === "Pending") && <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5 flex-shrink-0"></div>}
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs text-gray-500">#{ticket.id}</span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                    ticket.priority === "High" || ticket.priority === "Critical" ? "bg-red-100 text-red-700" : 
                                    ticket.priority === "Medium" ? "bg-yellow-100 text-yellow-700" :
                                    "bg-blue-100 text-blue-700"
                                }`}>{ticket.priority}</span>
                                {ticket.created_at && <span className="text-[10px] text-gray-400 ml-auto">{helpDeskUtils.getTimeAgo(ticket.created_at)}</span>}
                            </div>
                            <div className="flex items-center gap-2">
                                <Avatar name={ticket.employee_name} color="bg-blue-500" size="sm" />
                                <div className="flex-1 min-w-0">
                                    <span className="text-xs font-medium text-gray-700 block truncate">{ticket.employee_name}</span>
                                    <span className="text-[10px] text-gray-500">{ticket.employee_department}</span>
                                </div>
                            </div>
                        </div>
                    )) : (
                        <div className="p-4 text-center text-gray-400 text-sm">
                            <Search className="w-8 h-8 mx-auto mb-2 opacity-20" />
                            <p>No tickets found.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* --- CENTER PANEL: Workspace --- */}
            <div className="flex-1 flex flex-col bg-[#f8fafc] min-w-0">
                {/* Header */}
                <div className="h-16 bg-white border-b border-gray-200 flex justify-between items-center px-6 shadow-sm flex-shrink-0">
                    <div>
                        <h2 className="font-heading font-semibold text-lg text-gray-800 truncate pr-4">
                            {activeTicket?.subject || "Select a ticket"}
                        </h2>
                        {activeTicket && (
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                                <span className="bg-gray-100 px-2 py-0.5 rounded">{activeTicket.employee_department}</span>
                                <span>•</span>
                                <span>Raised by {activeTicket.employee_name}</span>
                            </div>
                        )}
                    </div>
                    
                    {activeTicket && (
                        <div className="flex items-center gap-3">
                            <AnimatePresence mode="wait">
                                {callStatus === "idle" && (
                                    <motion.button 
                                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                        onClick={handleStartCall}
                                        className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-full transition-colors flex items-center gap-2 group"
                                        title={`Call ${activeTicket.employee_contact}`}
                                    >
                                        <Phone className="w-5 h-5" />
                                        <span className="text-sm font-medium hidden group-hover:block max-w-0 group-hover:max-w-[100px] overflow-hidden whitespace-nowrap transition-all duration-300">Call Employee</span>
                                    </motion.button>
                                )}
                                
                                {callStatus === "connecting" && (
                                    <motion.div 
                                        initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
                                        className="bg-yellow-500 text-white px-4 py-2 rounded-full flex items-center gap-3 shadow-lg"
                                    >
                                        <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></span>
                                        <span className="text-sm font-medium">Dialing {activeTicket.employee_contact}...</span>
                                        <button onClick={() => setCallStatus("idle")} className="hover:bg-black/10 p-1 rounded-full"><X size={14}/></button>
                                    </motion.div>
                                )}

                                {callStatus === "active" && (
                                    <motion.div 
                                        initial={{ scale: 0.9 }} animate={{ scale: 1 }}
                                        className="bg-green-600 text-white px-4 py-2 rounded-full flex items-center gap-3 shadow-lg animate-pulse"
                                    >
                                        <span className="text-xs font-bold font-mono">{formatTime(callDuration)}</span>
                                        <span className="text-sm font-medium">In Call...</span>
                                        <button 
                                            onClick={handleEndCall}
                                            className="bg-red-500 hover:bg-red-600 p-1.5 rounded-full transition-colors shadow-md"
                                        >
                                            <PhoneCall className="w-4 h-4 text-white" />
                                        </button>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                            
                            <div className="h-6 w-px bg-gray-200 mx-1"></div>

                            {activeTicket.status !== "Closed" && activeTicket.status !== "Resolved" ? (
                                <button 
                                    onClick={handleResolve}
                                    className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm"
                                >
                                    <CheckCircle className="w-4 h-4" /> Resolve
                                </button>
                            ) : (
                                 <span className="flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-lg text-sm font-medium border border-green-200">
                                    <CheckCircle className="w-4 h-4" /> {activeTicket.status}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Chat Stream */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {activeTicket ? (
                        <>
                            <div className="flex justify-center">
                                <span className="bg-gray-200 text-gray-600 text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-wide">
                                    {activeTicket.created_at ? helpDeskUtils.getTimeAgo(activeTicket.created_at) : "Today"}
                                </span>
                            </div>

                            <AnimatePresence>
                                {activeMessages.map((msg) => (
                                    <motion.div 
                                        key={msg.id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className={`flex ${msg.message_type === "employee" ? "justify-start" : msg.message_type === "system" ? "justify-center" : "justify-end"}`}
                                    >
                                        {msg.message_type === "system" ? (
                                            <div className="bg-gray-100 border border-gray-200 text-gray-500 text-xs px-4 py-1.5 rounded-full italic flex items-center gap-2">
                                                <Clock className="w-3 h-3" /> {msg.content}
                                            </div>
                                        ) : (
                                            <div className={`max-w-[70%] group`}>
                                                <div className={`flex items-end gap-2 ${msg.message_type !== "employee" ? "flex-row-reverse" : ""}`}>
                                                    {/* Avatar */}
                                                    {msg.message_type === "employee" && <Avatar name={activeTicket.employee_name} color="bg-blue-500" size="sm" />}
                                                    {msg.message_type === "admin" && <Avatar name="Admin" color="bg-indigo-600" size="sm" />}
                                                    {msg.message_type === "internal" && <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center border border-yellow-300"><Lock className="w-4 h-4 text-yellow-600"/></div>}

                                                    {/* Bubble */}
                                                    <div className={`
                                                        p-4 rounded-2xl text-sm shadow-sm leading-relaxed relative
                                                        ${msg.message_type === "employee" ? "bg-white text-gray-800 rounded-bl-none border border-gray-100" : ""}
                                                        ${msg.message_type === "admin" ? "bg-indigo-600 text-white rounded-br-none" : ""}
                                                        ${msg.message_type === "internal" ? "bg-yellow-50 text-gray-800 border border-yellow-200 rounded-br-none" : ""}
                                                    `}>
                                                        {msg.message_type === "internal" && (
                                                            <div className="text-[10px] font-bold text-yellow-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                                                                <Lock className="w-3 h-3" /> Internal Note
                                                            </div>
                                                        )}
                                                        
                                                        <RenderMessageContent msg={msg} />

                                                        <div className={`text-[10px] mt-2 text-right opacity-70 ${msg.message_type === "admin" ? "text-indigo-100" : "text-gray-400"}`}>
                                                            {formatMessageTime(msg.created_at)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-center text-gray-400">
                            <div className="text-center">
                                <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-20" />
                                <p className="text-lg font-medium mb-2">Select a ticket to start</p>
                                <p className="text-sm">Choose a ticket from the left panel to view the conversation</p>
                            </div>
                        </div>
                    )}
                    <div ref={chatEndRef} />
                </div>

                {/* Input Area */}
                {activeTicket ? (
                    <div className={`p-4 border-t ${isInternalNote ? "bg-yellow-50/50 border-yellow-200" : "bg-white border-gray-200"} transition-colors duration-300`}>
                        
                        {/* Recording Status Overlay */}
                        <AnimatePresence>
                            {(isRecordingAudio || isRecordingVideo) && (
                                <motion.div 
                                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
                                    className="absolute bottom-20 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-6 py-3 rounded-full shadow-xl flex items-center gap-4 z-20"
                                >
                                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse-red"></div>
                                    <span className="font-mono font-medium">{formatTime(recordingTime)}</span>
                                    <span className="text-sm border-l border-gray-700 pl-4">{isRecordingAudio ? "Recording Audio..." : "Recording Video..."}</span>
                                    <button 
                                        onClick={isRecordingAudio ? toggleAudioRecording : toggleVideoRecording}
                                        className="ml-2 bg-white/20 hover:bg-white/30 p-1.5 rounded-full transition"
                                    >
                                        <StopCircle size={18} />
                                    </button>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Toggle Mode */}
                        <div className="flex items-center gap-4 mb-3 px-2">
                             <button 
                                onClick={() => setIsInternalNote(false)}
                                className={`text-xs font-medium flex items-center gap-1.5 pb-1 border-b-2 transition-all ${
                                    !isInternalNote ? "border-indigo-600 text-indigo-700" : "border-transparent text-gray-500 hover:text-gray-700"
                                }`}
                            >
                                <MessageSquare className="w-3.5 h-3.5" /> Public Reply
                            </button>
                            <button 
                                onClick={() => setIsInternalNote(true)}
                                className={`text-xs font-medium flex items-center gap-1.5 pb-1 border-b-2 transition-all ${
                                    isInternalNote ? "border-yellow-500 text-yellow-700" : "border-transparent text-gray-500 hover:text-gray-700"
                                }`}
                            >
                                <Lock className="w-3.5 h-3.5" /> Private Note
                            </button>
                        </div>

                        <div className="relative">
                            <textarea
                                value={inputValue}
                                onChange={handleInputChange}
                                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSendMessage())}
                                placeholder={isInternalNote ? "Add an internal note for HR admins only..." : "Type your reply to the employee..."}
                                className={`w-full rounded-xl pl-4 pr-12 py-3 text-sm font-body shadow-sm resize-none focus:ring-2 outline-none transition-all
                                    ${isInternalNote ? "bg-yellow-50 border-yellow-200 focus:ring-yellow-400 focus:border-yellow-400 text-gray-800 placeholder-yellow-700/50" 
                                    : "bg-gray-50 border-gray-200 focus:ring-indigo-500 focus:bg-white text-gray-800"}`}
                                rows={2}
                            />
                            <div className="absolute right-3 bottom-3 flex gap-2">
                                 <button 
                                    onClick={handleFileIconClick}
                                    className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 hover:bg-gray-100 rounded-lg"
                                    title="Attach File"
                                 >
                                    <Paperclip className="w-4 h-4" />
                                </button>
                                <button 
                                    onClick={handleSendMessage}
                                    disabled={!inputValue.trim()}
                                    className={`p-1.5 rounded-lg transition-all ${
                                        inputValue.trim() 
                                        ? (isInternalNote ? "bg-yellow-500 hover:bg-yellow-600 text-white" : "bg-indigo-600 hover:bg-indigo-700 text-white") 
                                        : "bg-gray-200 text-gray-400 cursor-not-allowed"
                                    }`}
                                >
                                    <Send className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                        <div className="flex justify-between items-center mt-2 px-1">
                            <div className="flex gap-2">
                                <button 
                                    onClick={toggleAudioRecording}
                                    className={`p-2 rounded-lg transition-colors flex items-center gap-2 text-xs font-medium ${isRecordingAudio ? "bg-red-50 text-red-600 border border-red-200" : "text-gray-500 hover:bg-gray-100"}`}
                                >
                                    <Mic className={`w-4 h-4 ${isRecordingAudio ? "animate-pulse" : ""}`}/> 
                                    {isRecordingAudio ? "Recording..." : ""}
                                </button>
                                <button 
                                    onClick={toggleVideoRecording}
                                    className={`p-2 rounded-lg transition-colors flex items-center gap-2 text-xs font-medium ${isRecordingVideo ? "bg-red-50 text-red-600 border border-red-200" : "text-gray-500 hover:bg-gray-100"}`}
                                >
                                    <Video className={`w-4 h-4 ${isRecordingVideo ? "animate-pulse" : ""}`}/>
                                    {isRecordingVideo ? "Recording..." : ""}
                                </button>
                            </div>
                            <span className="text-[10px] text-gray-400 font-body">
                                Press <strong>Enter</strong> to send
                            </span>
                        </div>
                    </div>
                ) : (
                    <div className="p-4 border-t bg-gray-50 text-center text-gray-500">
                        <p className="text-sm">Select a ticket to start messaging</p>
                    </div>
                )}
            </div>

            {/* --- RIGHT PANEL: Context --- */}
            {activeTicket && (
                <div className="w-72 bg-white border-l border-gray-200 flex flex-col flex-shrink-0 z-10 shadow-sm hidden xl:flex">
                    <div className="p-6 border-b border-gray-100 text-center">
                        <div className="flex justify-center mb-3">
                             <Avatar name={activeTicket.employee_name} color="bg-blue-500" size="lg" />
                        </div>
                        <h2 className="font-heading font-semibold text-lg text-gray-800">{activeTicket.employee_name}</h2>
                        <p className="font-body text-xs text-gray-500 mt-1">{activeTicket.employee_role || "Employee"}</p>
                        
                        <div className="flex justify-center gap-3 mt-4">
                            <button onClick={handleStartCall} className="p-2 rounded-full bg-gray-50 text-gray-600 hover:bg-indigo-50 hover:text-indigo-600 transition-colors border border-gray-100">
                                <Phone className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleInitiateCall(activeTicket.emp_code, 'video')} className="p-2 rounded-full bg-gray-50 text-gray-600 hover:bg-indigo-50 hover:text-indigo-600 transition-colors border border-gray-100">
                                <Video className="w-4 h-4" />
                            </button>
                            <button className="p-2 rounded-full bg-gray-50 text-gray-600 hover:bg-indigo-50 hover:text-indigo-600 transition-colors border border-gray-100">
                                <MoreHorizontal className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    <div className="p-5 flex-1 overflow-y-auto space-y-6">
                        {/* Contact Info */}
                        <div>
                            <h3 className="font-heading text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Contact Details</h3>
                            <div className="space-y-3 font-body text-sm">
                                {activeTicket.employee_contact && (
                                    <div className="flex items-center gap-3 text-gray-700 bg-gray-50 p-2 rounded-lg border border-gray-100">
                                        <Phone className="w-4 h-4 text-gray-400"/>
                                        <span className="font-mono text-xs">{activeTicket.employee_contact}</span>
                                    </div>
                                )}
                                <div className="flex items-center gap-3 text-gray-700 bg-gray-50 p-2 rounded-lg border border-gray-100">
                                    <Monitor className="w-4 h-4 text-gray-400"/>
                                    <span className="truncate text-xs">{activeTicket.employee_email}</span>
                                </div>
                            </div>
                        </div>

                        {/* Ticket Info */}
                        <div>
                            <h3 className="font-heading text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Ticket Details</h3>
                            <div className="space-y-2">
                                <div className="flex justify-between items-center p-2 rounded-lg bg-gray-50 border border-gray-100">
                                    <span className="text-xs text-gray-600">Status</span>
                                    <span className={`text-xs px-2 py-1 rounded font-medium ${helpDeskUtils.formatStatus(activeTicket.status)}`}>
                                        {activeTicket.status}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center p-2 rounded-lg bg-gray-50 border border-gray-100">
                                    <span className="text-xs text-gray-600">Priority</span>
                                    <span className={`text-xs px-2 py-1 rounded font-medium ${helpDeskUtils.formatPriority(activeTicket.priority)}`}>
                                        {activeTicket.priority}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center p-2 rounded-lg bg-gray-50 border border-gray-100">
                                    <span className="text-xs text-gray-600">Category</span>
                                    <span className="text-xs text-gray-700 font-medium">{activeTicket.category}</span>
                                </div>
                                {activeTicket.assigned_agent_name && (
                                    <div className="flex justify-between items-center p-2 rounded-lg bg-gray-50 border border-gray-100">
                                        <span className="text-xs text-gray-600">Assigned To</span>
                                        <span className="text-xs text-gray-700 font-medium">{activeTicket.assigned_agent_name}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Previous Tickets */}
                        <div>
                            <h3 className="font-heading text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Ticket History</h3>
                            <div className="space-y-2">
                                {tickets.filter(t => t.employee_name === activeTicket.employee_name && t.id !== activeTicket.id).slice(0, 3).map(t => (
                                    <div key={t.id} className="text-xs p-3 bg-white border border-gray-200 rounded-lg hover:border-indigo-300 transition-colors cursor-pointer group" onClick={() => handleTicketSelect(t.id)}>
                                        <div className="flex justify-between mb-1">
                                            <span className="font-medium text-gray-700 group-hover:text-indigo-600 truncate w-32">#{t.id.split('-').pop()}</span>
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] ${t.status==="Closed" || t.status==="Resolved" ?"bg-gray-100 text-gray-500":"bg-green-100 text-green-600"}`}>{t.status}</span>
                                        </div>
                                        <p className="text-gray-500 truncate">{t.subject}</p>
                                        <p className="text-gray-400 text-[10px] mt-1">{helpDeskUtils.getTimeAgo(t.created_at)}</p>
                                    </div>
                                ))}
                                 {tickets.filter(t => t.employee_name === activeTicket.employee_name && t.id !== activeTicket.id).length === 0 && (
                                    <p className="text-xs text-gray-400 italic">No other tickets found.</p>
                                 )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}